import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, devError, devWarn } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Race guard: kalau auth:expired pernah fire dalam window kecil ini, jangan
  // biarkan fetchMe (yang resolve telat dengan 200 cached/race) menghidupkan
  // kembali user yang sudah seharusnya null.
  const authExpiredAtRef = useRef(0);

  // SECURITY: Auth state ditentukan oleh httpOnly cookie (dibaca backend).
  // Frontend tidak tahu apakah cookie ada; cukup tanya backend via /auth/me.
  const fetchMe = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const { data } = await api.get("/auth/me");
      // Race guard: skip kalau auth:expired fired SETELAH fetchMe mulai.
      if (authExpiredAtRef.current > startedAt) return;
      setUser(data);
    } catch (e) {
      // 401 = belum login (normal). Selain itu → log error.
      if (e?.response?.status !== 401) {
        devError("Auth /me failed:", e?.message || e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Global 401 listener: kalau ada API call yang return 401 (cookie expired
  // / cleared / cross-site iframe Safari), langsung clear user agar
  // AdminLayout & ProtectedRoute kembali ke layar login. TIDAK ada toast.
  useEffect(() => {
    const handler = () => {
      authExpiredAtRef.current = Date.now();
      setUser(null);
    };
    window.addEventListener("auth:expired", handler);
    return () => window.removeEventListener("auth:expired", handler);
  }, []);

  const login = async (identifier, password) => {
    // Backend set httpOnly cookie via Set-Cookie header (TIDAK perlu simpan token di JS)
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data.user);
    try { sessionStorage.setItem("show_after_login_popup", "1"); } catch {}
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    try { sessionStorage.setItem("show_after_login_popup", "1"); } catch {}
    return data.user;
  };

  const logout = async () => {
    try {
      // Backend clear httpOnly cookie via Set-Cookie max-age=0
      await api.post("/auth/logout");
    } catch (e) {
      devWarn("Logout API failed:", e?.message || e);
    }
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, refresh: fetchMe, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
